import { useQueryClient } from '@tanstack/react-query';

import {
    useAcknowledgedAlertsController_acknowledgeAlert,
    useAcknowledgedAlertsController_getAcknowledgements,
    useAcknowledgedAlertsController_unacknowledgeAlert,
    useAlertsController_allAlerts,
} from '@/services/hooks';
import { AcknowledgedAlertsController_getAcknowledgements } from '@/services/services';
import type { Alert, AlertAcknowledgement as AlertAcknowledgementDto } from '@/services/types';

import { type DomainMutation, ignoreBody, useMappedMutation } from '../shared/mutation';
import { type DomainQuery, useMappedQuery } from '../shared/query';

import type { AlertAcknowledgement, AlertSilence, AlertUnsilence } from './types';

function body<T>(value: T): T {
    return value;
}

/** Every open bioscope alert. */
export function useGetAlerts(): DomainQuery<Alert[]> {
    return useMappedQuery(useAlertsController_allAlerts(), body<Alert[]>);
}

function toAcknowledgements(rows: AlertAcknowledgementDto[]): AlertAcknowledgement[] {
    return rows.map((row) => ({ ...row, alertId: row.alertId ?? null }));
}

/** The alerts someone has silenced, and until when. */
export function useGetAlertAcknowledgements(): DomainQuery<AlertAcknowledgement[]> {
    return useMappedQuery(
        useAcknowledgedAlertsController_getAcknowledgements(),
        toAcknowledgements
    );
}

function silenceToApi(silence: AlertSilence) {
    return { requestBody: silence };
}

function unsilenceToApi(unsilence: AlertUnsilence) {
    return { requestBody: unsilence };
}

/** Refetches the acknowledgements once a silence change settles. */
function useRefetchAcknowledgements(): { onSettled: () => void } {
    const queryClient = useQueryClient();
    return {
        onSettled: () => {
            queryClient.invalidateQueries({
                queryKey: [AcknowledgedAlertsController_getAcknowledgements.key],
            });
        },
    };
}

export function useSilenceAlert(): DomainMutation<AlertSilence, void> {
    return useMappedMutation(
        useAcknowledgedAlertsController_acknowledgeAlert(useRefetchAcknowledgements()),
        silenceToApi,
        ignoreBody
    );
}

export function useUnsilenceAlert(): DomainMutation<AlertUnsilence, void> {
    return useMappedMutation(
        useAcknowledgedAlertsController_unacknowledgeAlert(useRefetchAcknowledgements()),
        unsilenceToApi,
        ignoreBody
    );
}
